/*
 * Shared binding-ID wiring between a rendered dynamic widget and the debug
 * inspector. Widgets tag the element that actually shows a bound value with
 * these attributes — there is deliberately no second, hidden copy of the UI —
 * so the inspector can select a skeleton node from a rendered element and
 * highlight a rendered element from a skeleton node.
 */

export const DYNAMIC_WIDGET_ID_ATTRIBUTE = 'data-dynamic-widget-id';
export const DYNAMIC_WIDGET_BINDING_ID_ATTRIBUTE =
    'data-dynamic-widget-binding-id';
export const DYNAMIC_WIDGET_BINDING_SELECTED_ATTRIBUTE =
    'data-dynamic-widget-binding-selected';

// Matches the focus-ring convention used by the shadcn primitives
// (`focus-visible:ring-ring/50 focus-visible:ring-[3px]`). Applied
// imperatively so selecting a skeleton node does not re-render the widget.
const SELECTED_BINDING_CLASSES = [
    'ring-ring/50',
    'ring-[3px]',
    'rounded-sm',
] as const;

export type DynamicWidgetBindingProps = {
    readonly [DYNAMIC_WIDGET_ID_ATTRIBUTE]: string;
    readonly [DYNAMIC_WIDGET_BINDING_ID_ATTRIBUTE]: string;
};

export type DynamicWidgetBindingTarget = {
    readonly widgetId: string;
    readonly bindingId: string;
};

/**
 * Spread onto the element that renders a bound value or a bound control. Both
 * IDs are needed because binding IDs (`rows.0.patientName`) are only unique
 * inside one widget.
 */
export function dynamicWidgetBindingProps(
    widgetId: string,
    bindingId: string,
): DynamicWidgetBindingProps {
    return {
        [DYNAMIC_WIDGET_ID_ATTRIBUTE]: widgetId,
        [DYNAMIC_WIDGET_BINDING_ID_ATTRIBUTE]: bindingId,
    };
}

function escapeAttributeValue(value: string): string {
    return value.replace(/["\\]/g, '\\$&');
}

/**
 * First element rendering this binding. A binding can be rendered more than
 * once (one attempt row per caregiver, for example); the first match is the
 * one the inspector highlights.
 */
export function findRenderedDynamicWidgetBinding(
    target: DynamicWidgetBindingTarget,
): HTMLElement | null {
    if (typeof document === 'undefined') {
        return null;
    }
    return document.querySelector<HTMLElement>(
        `[${DYNAMIC_WIDGET_ID_ATTRIBUTE}="${escapeAttributeValue(target.widgetId)}"]` +
            `[${DYNAMIC_WIDGET_BINDING_ID_ATTRIBUTE}="${escapeAttributeValue(target.bindingId)}"]`,
    );
}

/** Reads the binding a click landed on, walking up from the event target. */
export function readDynamicWidgetBindingTarget(
    eventTarget: EventTarget | null,
): DynamicWidgetBindingTarget | null {
    if (!(eventTarget instanceof Element)) {
        return null;
    }
    const element = eventTarget.closest(
        `[${DYNAMIC_WIDGET_BINDING_ID_ATTRIBUTE}]`,
    );
    const widgetId = element?.getAttribute(DYNAMIC_WIDGET_ID_ATTRIBUTE);
    const bindingId = element?.getAttribute(
        DYNAMIC_WIDGET_BINDING_ID_ATTRIBUTE,
    );
    if (!widgetId || !bindingId) {
        return null;
    }
    return { widgetId, bindingId };
}

function clearHighlight(): void {
    if (typeof document === 'undefined') {
        return;
    }
    const previous = document.querySelectorAll<HTMLElement>(
        `[${DYNAMIC_WIDGET_BINDING_SELECTED_ATTRIBUTE}="true"]`,
    );
    previous.forEach((element) => {
        element.removeAttribute(DYNAMIC_WIDGET_BINDING_SELECTED_ATTRIBUTE);
        element.classList.remove(...SELECTED_BINDING_CLASSES);
    });
}

/**
 * Moves the highlight ring to the element rendering `target` and scrolls it
 * into view. Passing null only clears the previous highlight.
 */
export function highlightRenderedDynamicWidgetBinding(
    target: DynamicWidgetBindingTarget | null,
): HTMLElement | null {
    clearHighlight();
    if (target === null) {
        return null;
    }
    const element = findRenderedDynamicWidgetBinding(target);
    if (element === null) {
        return null;
    }
    element.setAttribute(DYNAMIC_WIDGET_BINDING_SELECTED_ATTRIBUTE, 'true');
    element.classList.add(...SELECTED_BINDING_CLASSES);
    // jsdom has no layout, so it does not implement scrollIntoView.
    if (typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    return element;
}
